<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime } from 'date-fns';
import { useTasksStore } from 'dashboard/stores/tasks';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import TaskDetailsDialog from 'dashboard/components-next/Tasks/TaskDetailsDialog.vue';

const { t } = useI18n();
const tasksStore = useTasksStore();
const currentUser = useMapGetter('getCurrentUser');

const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    await tasksStore.get({ assigned_to_id: currentUser.value.id });
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchData);

const sortedTasks = computed(() =>
  [...tasksStore.getTasksList].sort((a, b) => a.dueDate - b.dueDate)
);

const relatedToLabel = task =>
  task.contact?.name || task.company?.name || task.deal?.name || '—';

const statusLabelKey = status =>
  `DASHBOARD.MEU_ESPACO.TASKS.STATUS_${status.toUpperCase()}`;

const detailsDialogRef = ref(null);
const selectedTask = ref(null);
const isUpdating = computed(() => tasksStore.uiFlags.updatingItem);
const isDeleting = computed(() => tasksStore.uiFlags.deletingItem);

const openTask = task => {
  selectedTask.value = task;
  detailsDialogRef.value?.open();
};

const toggleSelectedTaskStatus = async () => {
  if (!selectedTask.value) return;
  const newStatus =
    selectedTask.value.status === 'completed' ? 'pending' : 'completed';
  try {
    const updated = await tasksStore.update({
      id: selectedTask.value.id,
      status: newStatus,
    });
    selectedTask.value = updated;
    useAlert(t('TASKS.LIST.UPDATE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('TASKS.LIST.UPDATE.ERROR_MESSAGE'));
  }
};

const handleUpdateSelectedTask = updated => {
  selectedTask.value = updated;
  useAlert(t('TASKS.LIST.UPDATE.SUCCESS_MESSAGE'));
};

const handleDeleteSelectedTask = async id => {
  try {
    await tasksStore.delete(id);
    detailsDialogRef.value?.close();
    useAlert(t('TASKS.LIST.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('TASKS.LIST.DELETE.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.MEU_ESPACO.SECTIONS.TASKS') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.MEU_ESPACO.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.MEU_ESPACO.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.MEU_ESPACO.LOADING') }}
    </p>
    <p
      v-else-if="!sortedTasks.length"
      class="px-3 pb-3 text-sm text-n-slate-10"
    >
      {{ t('DASHBOARD.MEU_ESPACO.EMPTY_STATE') }}
    </p>
    <div v-else class="px-3 pb-3 overflow-y-auto overflow-x-auto max-h-80">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-10">
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.TASKS.TITLE') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.TASKS.RELATED_TO') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.TASKS.DUE_DATE') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.TASKS.STATUS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="task in sortedTasks"
            :key="task.id"
            class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
            @click="openTask(task)"
          >
            <td class="py-1 pr-3 text-n-slate-12">{{ task.title }}</td>
            <td class="py-1 pr-3">{{ relatedToLabel(task) }}</td>
            <td class="py-1 pr-3">
              {{ format(fromUnixTime(task.dueDate), 'dd/MM/yyyy') }}
            </td>
            <td class="py-1 pr-3">{{ t(statusLabelKey(task.status)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <TaskDetailsDialog
      ref="detailsDialogRef"
      :task="selectedTask"
      :is-updating="isUpdating"
      :is-deleting="isDeleting"
      @toggle-status="toggleSelectedTaskStatus"
      @update-task="handleUpdateSelectedTask"
      @delete-task="handleDeleteSelectedTask"
    />
  </section>
</template>
